import * as React from "react"
import type { AlfredDeviceListControlsState } from "@/lib/alfred-device-navigation"
import type {
  AlfredDeviceListParams,
  AlfredDeviceSortField,
  SortOrder,
} from "@/types/alfred-device"

export interface AlfredDeviceListControls {
  /** Current 1-indexed page. */
  page: number
  /** Rows per page. */
  pageSize: number
  /** Committed search term sent to the API; only changes on submit or clear. */
  filter: string
  /** Live value of the search box, before the user commits it. */
  searchInput: string
  /** Active sort column, or null for the API default (deviceId ascending). */
  sortBy: AlfredDeviceSortField | null
  sortOrder: SortOrder
  /** Params object ready to hand to the list hooks. */
  params: AlfredDeviceListParams
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  setSearchInput: (value: string) => void
  /** Commit the current search box value as the active filter (button / Enter). */
  submitSearch: () => void
  /** Clear both the search box and the active filter (the "x" button). */
  clearSearch: () => void
  /** Toggle direction when re-clicking the active column, else sort the new column ascending. */
  handleSort: (field: AlfredDeviceSortField) => void
}

// Shared state for the Alfred device list views. Search is committed explicitly so
// we don't hit the API on every keystroke, and the committed filter/sort persist as
// the user pages through results (they only reset page position, not the criteria).
export function useAlfredDeviceListControls(
  initialState?: AlfredDeviceListControlsState,
): AlfredDeviceListControls {
  const [page, setPage] = React.useState(() => initialState?.page ?? 1)
  const [pageSize, setPageSizeState] = React.useState(() => initialState?.pageSize ?? 50)
  const [searchInput, setSearchInput] = React.useState(() => initialState?.searchInput ?? "")
  const [filter, setFilter] = React.useState(() => initialState?.filter ?? "")
  const [sortBy, setSortBy] = React.useState<AlfredDeviceSortField | null>(
    () => initialState?.sortBy ?? null,
  )
  const [sortOrder, setSortOrder] = React.useState<SortOrder>(
    () => initialState?.sortOrder ?? "asc",
  )

  const setPageSize = React.useCallback((size: number) => {
    setPageSizeState(size)
    setPage(1)
  }, [])

  const submitSearch = React.useCallback(() => {
    setFilter(searchInput.trim())
    setPage(1)
  }, [searchInput])

  const clearSearch = React.useCallback(() => {
    setSearchInput("")
    setFilter("")
    setPage(1)
  }, [])

  const handleSort = React.useCallback(
    (field: AlfredDeviceSortField) => {
      if (sortBy === field) {
        setSortOrder((d) => (d === "asc" ? "desc" : "asc"))
      } else {
        setSortBy(field)
        setSortOrder("asc")
      }
      setPage(1)
    },
    [sortBy],
  )

  const params: AlfredDeviceListParams = {
    page,
    pageSize,
    filter: filter || undefined,
    sortBy: sortBy ?? undefined,
    sortOrder: sortBy ? sortOrder : undefined,
  }

  return {
    page,
    pageSize,
    filter,
    searchInput,
    sortBy,
    sortOrder,
    params,
    setPage,
    setPageSize,
    setSearchInput,
    submitSearch,
    clearSearch,
    handleSort,
  }
}
